import styled, { css } from "styled-components";
import { ionVerticalGradientText } from "../styles/ionVerticalGradientText";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { revealStyle, fadeBlurRevealStyle } from "../styles/scrollReveal";
import { SectionTitle } from "./common/SectionTitle";
import NotificationCompleteImg from "../../../assets/images/pluvaIonLanding/feature03_notification_complete.png";
import StatsProgressImg from "../../../assets/images/pluvaIonLanding/feature03_stats_progress.png";
import TabletEntertainmentImg from "../../../assets/images/pluvaIonLanding/feature03_tablet_entertainment.png";
import TabletSplitNavImg from "../../../assets/images/pluvaIonLanding/feature03_tablet_split_nav.png";

export const WorkExperienceSection = () => {
  const titleReveal = useScrollReveal(0.3);
  const splitReveal = useScrollReveal(0.35);
  const smartReveal = useScrollReveal(0.35);

  return (
    <Container>
      <SectionTitle ref={titleReveal.ref} $visible={titleReveal.isVisible}>
        {"차원이 다른 작업 경험"}
      </SectionTitle>

      <MainTitle $visible={titleReveal.isVisible}>
        {"손은 자유롭게\n작업은 정확하게"}
      </MainTitle>

      {/* 분할 화면 */}
      <FeatureBlock>
        <FeatureLabel>{"분할 화면"}</FeatureLabel>
        <FeatureDesc>{"작업 화면과 영상까지\n한눈에"}</FeatureDesc>
      </FeatureBlock>

      <SplitScreenWrapper
        ref={splitReveal.ref}
        $visible={splitReveal.isVisible}
      >
        <ScreenOverlay
          $visible={splitReveal.isVisible}
          $delay={400}
          src={TabletEntertainmentImg}
          alt="분할 화면 엔터테인먼트"
          loading="lazy"
        />
        <TabletBase
          $visible={splitReveal.isVisible}
          $delay={0}
          src={TabletSplitNavImg}
          alt="태블릿 내비게이션"
          loading="lazy"
        />
      </SplitScreenWrapper>

      {/* 스마트 안내 */}
      <FeatureBlock $alignRight>
        <FeatureLabel $alignRight>{"스마트 안내"}</FeatureLabel>
        <FeatureDesc $alignRight>
          {"진행률 · 날씨 · 완료 시간\n자동 안내"}
        </FeatureDesc>
      </FeatureBlock>

      <SmartGuideWrapper ref={smartReveal.ref} $visible={smartReveal.isVisible}>
        <FloatingCardsRow>
          <CardFirst
            $visible={smartReveal.isVisible}
            $delay={400}
            src={StatsProgressImg}
            alt="작업율 통계"
            loading="lazy"
          />
          <CardSecond
            $visible={smartReveal.isVisible}
            $delay={700}
            src={NotificationCompleteImg}
            alt="작업 완료 알림"
            loading="lazy"
          />
        </FloatingCardsRow>
        <TabletSmartBase
          $visible={smartReveal.isVisible}
          $delay={0}
          src={TabletSplitNavImg}
          alt="스마트 안내 태블릿"
          loading="lazy"
        />
      </SmartGuideWrapper>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;

  padding: 56px 0 80px;

  background-color: #000000;
`;

const MainTitle = styled.h3<{ $visible: boolean }>`
  text-align: center;
  white-space: pre-line;
  align-self: stretch;

  font-family: Pretendard;
  font-size: 28px;
  font-weight: 700;
  line-height: 130%;

  ${ionVerticalGradientText}

  padding: 0 24px;
  margin-bottom: 48px;

  ${revealStyle}
`;

const FeatureBlock = styled.div<{ $alignRight?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: ${({ $alignRight }) =>
    $alignRight ? "flex-end" : "flex-start"};

  padding: 0 24px;
  margin-bottom: 70px;
`;

const FeatureLabel = styled.span<{ $alignRight?: boolean }>`
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 600;
  line-height: 130%;
  text-align: ${({ $alignRight }) => ($alignRight ? "right" : "left")};

  color: #ffffff;

  margin-bottom: 20px;
`;

const FeatureDesc = styled.p<{ $alignRight?: boolean }>`
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 600;
  line-height: 130%;
  text-align: ${({ $alignRight }) => ($alignRight ? "right" : "left")};
  white-space: pre-line;

  color: #c3d7e2;
`;

/* 분할 화면: 화면이미지 위, 태블릿 아래 */
const SplitScreenWrapper = styled.div<{ $visible: boolean }>`
  width: 100%;
  position: relative;
  margin-bottom: 100px;
  padding: 0 24px;
  padding-bottom: 100px;
  perspective: 800px;

  &::after {
    content: "";
    position: absolute;
    bottom: 0px;
    left: 55%;
    width: 70%;
    height: 85px;
    background: radial-gradient(
      ellipse at center,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 90%
    );
    border-radius: 50%;
    filter: blur(12px);
    pointer-events: none;
    transform: translateX(-50%) skewX(10deg) rotate(-20deg);
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 0.6s ease;
  }
`;

const ScreenOverlay = styled.img<{ $visible: boolean; $delay: number }>`
  width: 70%;
  display: block;
  margin-left: 12%;
  object-fit: contain;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.6));
  position: relative;
  z-index: 2;
  margin-bottom: -24px;
  transform: skewX(15deg) rotate(-20deg) rotateY(10deg);

  ${fadeBlurRevealStyle}
`;

const TabletBase = styled.img<{ $visible: boolean; $delay: number }>`
  width: 70%;
  display: block;
  object-fit: contain;
  filter: drop-shadow(0 16px 32px rgba(0, 0, 0, 0.8));
  position: relative;
  margin-left: 17%;
  z-index: 1;
  transform: skewX(15deg) rotate(-20deg) rotateY(10deg);

  ${fadeBlurRevealStyle}
`;

/* 스마트 안내 */
const SmartGuideWrapper = styled.div<{ $visible: boolean }>`
  width: 100%;
  padding: 0 24px;
  padding-bottom: 100px;
  margin-bottom: 100px;
  perspective: 800px;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 10px;
    left: 55%;
    width: 70%;
    height: 70px;
    background: radial-gradient(
      ellipse at center,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 85%
    );
    border-radius: 50%;
    filter: blur(12px);
    pointer-events: none;
    transform: translateX(-50%) skewX(15deg) rotate(-20deg);
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 0.6s ease;
  }
`;

const FloatingCardsRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: -24px;
  position: relative;
  z-index: 2;
  padding-left: 8px;
`;

const cardBaseStyle = css`
  width: 55%;
  display: block;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  transform: skewX(15deg) rotate(-20deg) rotateY(10deg);
`;

const CardFirst = styled.img<{ $visible: boolean; $delay: number }>`
  ${cardBaseStyle}
  margin-bottom: -20px;
  margin-left: 7%;
  z-index: 1;
  position: relative;

  ${fadeBlurRevealStyle}
`;

const CardSecond = styled.img<{ $visible: boolean; $delay: number }>`
  ${cardBaseStyle}
  margin-left: 38%;
  margin-top: -40px;
  z-index: 2;
  position: relative;

  ${fadeBlurRevealStyle}
`;

const TabletSmartBase = styled.img<{ $visible: boolean; $delay: number }>`
  width: 70%;
  display: block;
  margin-left: 17%;
  margin-top: 24px;
  object-fit: contain;
  filter: drop-shadow(0 16px 32px rgba(0, 0, 0, 0.8));
  position: relative;
  z-index: 1;
  transform: skewX(15deg) rotate(-20deg) rotateY(10deg);

  ${fadeBlurRevealStyle}
`;
